import React, { useEffect, useState } from 'react';
import { CheckCircle2Icon } from 'lucide-react';
import { DS } from '../../ds';

// Shortname de tu cuenta de Disqus (Disqus → Settings → General).
const DISQUS_SHORTNAME = 'alinova-blog';
const DISQUS_ENABLED = true;
const QA_HEADER_IMAGE = 'https://cdn.magicpatterns.com/patterns/generated-images/39ed121d-1fe9-4ece-a113-c0ab633ed16a.jpg';
const guidelines = ['Sé respetuoso con otros profesionales', 'Comparte experiencias clínicas reales', 'Haz preguntas específicas sobre ortodoncia invisible', 'No promuevas otros productos competidores', 'Las respuestas son validadas por el equipo Alinova'];
interface StoredQuestion {
  id: number;
  name: string;
  question: string;
  date: string;
}
const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '13px 16px',
  border: `1.5px solid ${DS.color.border}`,
  borderRadius: '12px',
  fontFamily: DS.font.body,
  fontSize: '14px',
  color: DS.color.ink,
  boxSizing: 'border-box',
  outline: 'none',
  transition: `border-color 0.2s ${DS.ease.smooth}`
};
export const BlogQA = () => {
  const [loadError, setLoadError] = useState(false);
  const [showForm, setShowForm] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    question: ''
  });
  useEffect(() => {
    if (!DISQUS_ENABLED) return;
    const pageUrl = window.location.href;
    const pageIdentifier = 'alinova-blog-qa';

    // Cualquier excepción lanzada por el script de terceros durante la carga
    // se captura aquí para no propagarse al resto de la app.
    const handleWindowError = (event: ErrorEvent) => {
      if (event.filename && event.filename.includes('disqus.com')) {
        event.preventDefault();
        setLoadError(true);
      }
    };
    window.addEventListener('error', handleWindowError);
    let script: HTMLScriptElement | null = null;
    try {
      ;
      (window as unknown as Record<string, unknown>).disqus_config = function config(this: {
        page: {
          url: string;
          identifier: string;
        };
      }) {
        this.page.url = pageUrl;
        this.page.identifier = pageIdentifier;
      };
      script = document.createElement('script');
      script.src = `https://${DISQUS_SHORTNAME}.disqus.com/embed.js`;
      script.setAttribute('data-timestamp', String(+new Date()));
      script.async = true;
      script.onerror = () => setLoadError(true);
      document.body.appendChild(script);
    } catch {
      setLoadError(true);
    }
    const hideReactionEmojis = window.setInterval(() => {
      const reactions = document.querySelectorAll<HTMLElement>('#disqus_thread .dsq-reaction, #disqus_thread .dsq-reaction-button, #disqus_thread .dsq-reaction-count');
      if (reactions.length > 0) {
        reactions.forEach(node => {
          node.style.display = 'none';
        });
        window.clearInterval(hideReactionEmojis);
      }
    }, 1000);
    return () => {
      window.removeEventListener('error', handleWindowError);
      window.clearInterval(hideReactionEmojis);
      script?.remove();
    };
  }, []);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      const stored: StoredQuestion[] = JSON.parse(window.localStorage.getItem('alinova_blog_questions') || '[]');
      stored.push({
        ...formData,
        date: new Date().toLocaleDateString('es-CO'),
        id: Date.now()
      });
      window.localStorage.setItem('alinova_blog_questions', JSON.stringify(stored));
    } catch {
      // localStorage puede no estar disponible (modo privado); no bloqueamos el envío.
    }
    setFormData({
      name: '',
      question: ''
    });
    setSubmitted(true);
  };
  return <div data-id="element-277" data-mp="2wa83kfw57p0y">
      <div style={{
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 320px)',
      gap: '32px',
      alignItems: 'center',
      marginBottom: '40px'
    }} className="qa-header-grid" data-id="element-278" data-mp="2w7vp4cplfwsn">
        <div data-id="element-279" data-mp="6kqxvrdwva4h">
          <h2 style={{
          fontFamily: DS.font.heading,
          fontSize: '26px',
          fontWeight: 700,
          color: DS.color.ink,
          margin: '0 0 12px'
        }} data-id="element-280" data-mp="6l91j92wkqmf">
            Preguntas &amp; Respuestas
          </h2>
          <p style={{
          fontFamily: DS.font.body,
          fontSize: '15px',
          color: DS.color.body,
          margin: 0,
          maxWidth: '520px'
        }} data-id="element-281" data-mp="354pmeki4pe63">
            Haz preguntas, comparte conocimiento y conecta con otros
            profesionales en ortodoncia invisible.
          </p>
        </div>

        <div style={{
        width: '100%',
        aspectRatio: '4 / 3',
        borderRadius: DS.radius.cardLg,
        overflow: 'hidden',
        background: DS.color.linen
      }} data-id="element-282" data-mp="uc4o9rsi4vmt">
          <img src={QA_HEADER_IMAGE} alt="Profesionales de la salud dental discutiendo un caso clínico" style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover'
        }} data-id="element-283" data-mp="16vjstb6hkyyp" />
        </div>
      </div>

      <div style={{
      padding: '24px',
      background: DS.color.mint,
      borderRadius: DS.radius.cardLg,
      marginBottom: '40px'
    }} data-id="element-284" data-mp="1kldfpnyfl95s">
        <h3 style={{
        fontFamily: DS.font.heading,
        fontSize: '15px',
        fontWeight: 700,
        color: DS.color.tealDeep,
        margin: '0 0 14px'
      }} data-id="element-285" data-mp="1pmazu6304d9x">
          Normas de la comunidad
        </h3>
        <ul style={{
        margin: 0,
        padding: 0,
        listStyle: 'none',
        display: 'grid',
        gap: '10px'
      }} data-id="element-286" data-mp="23ioi3hfd0ipg">
          {guidelines.map(rule => <li key={rule} style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '10px',
          fontFamily: DS.font.body,
          fontSize: '14px',
          color: DS.color.tealDeep
        }} data-id="element-287" data-mp="2i4kgoyxc30lm">
              <span aria-hidden="true" style={{
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            background: DS.color.tealDeep,
            marginTop: '7px',
            flexShrink: 0
          }} data-id="element-288" data-mp="1z7sofzztdb5f" />
              {rule}
            </li>)}
        </ul>
      </div>

      {/* Formulario rápido */}
      {showForm && <div style={{
      padding: '32px',
      background: '#fff',
      border: `2px solid ${DS.color.aqua}`,
      borderRadius: DS.radius.cardLg,
      marginBottom: '40px'
    }} data-id="element-289" data-mp="1ggt3d9vfwlhf">
          {submitted ? <div style={{
        textAlign: 'center',
        padding: '24px 0'
      }} data-id="element-290" data-mp="3s3mwez6rrb42">
              <div style={{
          width: '52px',
          height: '52px',
          borderRadius: '50%',
          background: DS.color.mint,
          color: DS.color.tealDeep,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 16px'
        }} data-id="element-291" data-mp="y3mo1obplx7">
                <CheckCircle2Icon size={26} aria-hidden="true" data-id="element-292" data-mp="3df8ls2342icy" />
              </div>
              <h3 style={{
          fontFamily: DS.font.heading,
          fontSize: '18px',
          fontWeight: 700,
          color: DS.color.ink,
          margin: '0 0 8px'
        }} data-id="element-293" data-mp="3i7s8ppnoupuf">
                Pregunta enviada
              </h3>
              <p style={{
          fontFamily: DS.font.body,
          fontSize: '14px',
          color: DS.color.body,
          margin: '0 0 20px'
        }} data-id="element-294" data-mp="1j3n7nu6yagaw">
                El equipo Alinova te responderá pronto.
              </p>
              <button type="button" onClick={() => {
          setSubmitted(false);
          setShowForm(false);
        }} style={{
          background: 'transparent',
          border: `1.5px solid ${DS.color.border}`,
          color: DS.color.teal,
          padding: '10px 22px',
          borderRadius: DS.radius.pill,
          fontFamily: DS.font.body,
          fontSize: '13px',
          fontWeight: 700,
          cursor: 'pointer'
        }} data-id="element-295" data-mp="1o0wwb7mimnxo">
                Ver preguntas de la comunidad
              </button>
            </div> : <>
              <h3 style={{
          fontFamily: DS.font.heading,
          fontSize: '18px',
          fontWeight: 700,
          color: DS.color.ink,
          margin: '0 0 6px'
        }} data-id="element-296" data-mp="34esl3sh1g95g">
                Haz tu pregunta rápidamente
              </h3>
              <p style={{
          fontFamily: DS.font.body,
          fontSize: '13px',
          color: DS.color.muted,
          margin: '0 0 22px'
        }} data-id="element-297" data-mp="2gnwngepwijqa">
                Sin necesidad de registro. Solo tu nombre y tu pregunta.
              </p>

              <form onSubmit={handleSubmit} style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '14px'
        }} data-id="element-298" data-mp="2bniqbrpu8u71">
                <input type="text" name="name" placeholder="Tu nombre completo" value={formData.name} onChange={handleChange} required style={inputStyle} onFocus={e => {
            e.currentTarget.style.borderColor = DS.color.aqua;
          }} onBlur={e => {
            e.currentTarget.style.borderColor = DS.color.border;
          }} data-id="element-299" data-mp="22nvlsx1tb89r" />
                <textarea name="question" placeholder="Tu pregunta sobre ortodoncia invisible..." value={formData.question} onChange={handleChange} required rows={4} style={{
            ...inputStyle,
            resize: 'vertical',
            minHeight: '110px'
          }} onFocus={e => {
            e.currentTarget.style.borderColor = DS.color.aqua;
          }} onBlur={e => {
            e.currentTarget.style.borderColor = DS.color.border;
          }} data-id="element-300" data-mp="hzyqecqdbrt9" />

                <div style={{
            display: 'flex',
            gap: '12px',
            flexWrap: 'wrap'
          }} data-id="element-301" data-mp="2gik17akzz0h6">
                  <button type="submit" style={{
              background: '#00AAD4',
              boxShadow: '0 8px 26px rgba(0, 170, 212, 0.42)',
              color: '#fff',
              border: 'none',
              padding: '12px 28px',
              borderRadius: DS.radius.pill,
              fontFamily: DS.font.body,
              fontSize: '14px',
              fontWeight: 700,
              cursor: 'pointer',
              transition: `background-color 0.22s ${DS.ease.smooth}, box-shadow 0.22s ${DS.ease.smooth}`
            }} onMouseEnter={e => {
              e.currentTarget.style.background = '#0099CC';
              e.currentTarget.style.boxShadow = '0 12px 34px rgba(0, 170, 212, 0.55)';
            }} onMouseLeave={e => {
              e.currentTarget.style.background = '#00AAD4';
              e.currentTarget.style.boxShadow = '0 8px 26px rgba(0, 170, 212, 0.42)';
            }} data-id="element-302" data-mp="2u8x7l8i8q74e">
                    Enviar pregunta
                  </button>

                  <button type="button" onClick={() => setShowForm(false)} style={{
              background: 'transparent',
              border: `1.5px solid ${DS.color.border}`,
              color: DS.color.body,
              padding: '12px 22px',
              borderRadius: DS.radius.pill,
              fontFamily: DS.font.body,
              fontSize: '13px',
              fontWeight: 700,
              cursor: 'pointer',
              transition: `border-color 0.2s ${DS.ease.smooth}, color 0.2s ${DS.ease.smooth}`
            }} onMouseEnter={e => {
              e.currentTarget.style.borderColor = DS.color.teal;
              e.currentTarget.style.color = DS.color.teal;
            }} onMouseLeave={e => {
              e.currentTarget.style.borderColor = DS.color.border;
              e.currentTarget.style.color = DS.color.body;
            }} data-id="element-303" data-mp="1wsr4jxhdgwhl">
                    Ver preguntas de la comunidad
                  </button>
                </div>
              </form>
            </>}
        </div>}

      {/* Hilo de la comunidad */}
      <div data-id="element-304" data-mp="8x329idiujk1">
        <h3 style={{
        fontFamily: DS.font.heading,
        fontSize: '18px',
        fontWeight: 700,
        color: DS.color.ink,
        margin: '0 0 6px'
      }} data-id="element-305" data-mp="8wt95lx6jwa4">
          Preguntas de la comunidad
        </h3>
        <p style={{
        fontFamily: DS.font.body,
        fontSize: '13px',
        color: DS.color.muted,
        margin: '0 0 20px'
      }} data-id="element-306" data-mp="242c5stzp3hbc">
          Respuestas de profesionales y del equipo Alinova.
        </p>

        {DISQUS_ENABLED && !loadError ? <div style={{
        background: '#fff',
        border: `1px solid ${DS.color.border}`,
        borderRadius: DS.radius.cardLg,
        padding: '20px'
      }} data-id="element-307" data-mp="1z76ax5obesb4">
            <div id="disqus_thread" data-id="element-308" data-mp="1bc3lowllmmu4" />
            <noscript data-id="element-309" data-mp="1bd4yp6m6djeh">
              Por favor habilita JavaScript para ver los comentarios.
            </noscript>
          </div> : <div style={{
        background: '#fff',
        border: `1px dashed ${DS.color.border}`,
        borderRadius: DS.radius.cardLg,
        padding: '40px 24px',
        textAlign: 'center'
      }} data-id="element-310" data-mp="35plbkq9h7vsb">
            <h4 style={{
          fontFamily: DS.font.heading,
          fontSize: '16px',
          fontWeight: 700,
          color: DS.color.ink,
          margin: '0 0 10px'
        }} data-id="element-311" data-mp="3ksuxws3ts3ms">
              No fue posible cargar el hilo de comentarios
            </h4>
            <p style={{
          fontFamily: DS.font.body,
          fontSize: '14px',
          color: DS.color.body,
          margin: '0 0 20px',
          maxWidth: '440px',
          marginLeft: 'auto',
          marginRight: 'auto',
          lineHeight: 1.6
        }} data-id="element-312" data-mp="7dgejrr7dznq">
              Usa el formulario de arriba para enviarnos tu pregunta
              directamente mientras se restablece la conexión.
            </p>
            <a href={DS.wa} target="_blank" rel="noopener noreferrer" style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          padding: '12px 26px',
          borderRadius: DS.radius.pill,
          background: '#00AAD4',
          boxShadow: '0 8px 26px rgba(0, 170, 212, 0.42)',
          color: '#fff',
          fontFamily: DS.font.body,
          fontSize: '14px',
          fontWeight: 700,
          textDecoration: 'none'
        }} data-id="element-313" data-mp="2e4fxutkqjtu6">
              Escríbenos por WhatsApp
            </a>
          </div>}
      </div>

      <style data-id="element-314" data-mp="19tili9xhghwf">
        {`
          @media (max-width: 720px) {
            .qa-header-grid {
              grid-template-columns: 1fr !important;
            }
          }
        `}
      </style>
    </div>;
};